import React, { useCallback, useState } from 'react';
import { Alert, Container, HeaderSection, Button } from '@groupelacentrale/contact-storybook';
import { IconPhoto, IconTip } from '@groupelacentrale/contact-storybook/lib/icons';
import { PhotoContainer } from 'components/Photo/PhotoContainer/PhotoContainer';
import { PhotoCharter } from 'components/Photo/PhotoCharter/PhotoCharter';
import { formatToMo } from 'utils/files';
import { useDropZone } from 'hooks';
import './PhotoFormView.scss';
import { Tracking } from 'api';

interface Props {
  values: {
    files: (File | string)[];
  };
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => void;
  isSubmitting: boolean;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  errorMessage?: string;

  max: number;
  maxFileSize: number;
  showActions?: boolean;
  onReturn: () => void | undefined;
}

export const PhotoFormView: React.FC<Props> = ({
  values: { files },
  setFieldValue,
  isSubmitting,
  handleSubmit,
  errorMessage,
  max,
  maxFileSize,
  showActions = true,
  onReturn,
}) => {
  const [errors, setErrors] = useState<string[]>([]);

  const setFiles: React.Dispatch<React.SetStateAction<(File | string)[]>> = useCallback(
    (value: React.SetStateAction<(File | string)[]>) => {
      if (setFieldValue) {
        if (!Array.isArray(value)) setFieldValue('files', value(files));
        else setFieldValue('files', value);
      }
    },
    [files, setFieldValue],
  );

  const add = useCallback(
    (fileList: FileList) => {
      const newFiles = [...files];
      const errors: string[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList.item(i)!;
        if (newFiles.length >= max) {
          errors.push(`${file.name} : Maximum atteint`);
        } else if (files.map(f => (f instanceof File ? f.name : f)).includes(file.name)) {
          errors.push(`${file.name} : déjà ajoutée`);
        } else if (!['image/jpeg', 'image/png'].includes(file.type)) {
          errors.push(`${file.name} : format d'image invalide`);
        } else if (maxFileSize && file.size > maxFileSize) {
          errors.push(`${file.name} : taille d'image > ${formatToMo(maxFileSize)} Mo`);
        } else {
          newFiles.push(file);
        }
      }
      setFiles(newFiles);
      setErrors(errors);
      if (errors.length !== 0) {
        Tracking.sendCurrentPageError();
      }
    },
    [max, maxFileSize, files, setFiles],
  );

  const remove = useCallback(
    (name: string) => {
      setErrors([]);
      setFiles(old => [...old].filter(file => (file instanceof File ? file.name !== name : name !== file)));
    },
    [setFiles],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (e?.dataTransfer?.files) {
        add(e.dataTransfer.files);
      }
    },
    [add],
  );

  const dropZone = useDropZone(handleDrop);
  const dropZoneClass = dropZone.isDropZoneVisible ? 'show' : '';

  return (
    <Container shadowed={showActions} paddings="mediumP" className="photo-form-container" backgroundColor="bgWhite">
      <div className={`photo-drop-zone ${dropZoneClass}`} {...dropZone.dragHandlers}>
        <IconPhoto width={70} color="blue" />
        Déposer vos photos ici
      </div>
      {showActions && <HeaderSection noUnderline>Photos de votre véhicule</HeaderSection>}
      <p className="photo-info">
        <em>
          <IconTip width={18} color="blue" />
          &nbsp;Une annonce avec photo est 7 fois plus vue.&nbsp;
        </em>
        Vos photos seront classées automatiquement avant la mise en ligne.
        <br />
        Poids maximum par photo : {formatToMo(maxFileSize)} Mo
        <br />
        Formats acceptés : JPG, JPEG ou PNG
        <br />
        <PhotoCharter maxFileSize={formatToMo(maxFileSize)} />
      </p>
      <PhotoContainer files={files} errors={errors} max={max} onAdd={add} onRemove={remove} />
      {errorMessage && <Alert type="error">{errorMessage}</Alert>}
      {showActions && (
        <div className="actions">
          <Button label="Retour" theme="quaternary" type="button" onClick={onReturn} />
          <Button
            label="Valider et continuer"
            type="button"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      )}
    </Container>
  );
};
